import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from "react";

import { useAssetsStore } from "src/hooks/snug/use-assets";
import { usePeopleRolesStore } from "src/hooks/snug/use-peopleRoles";
import { useHistoryStore } from "src/hooks/snug/use-histories";
import { useIdentificationStore } from "src/hooks/snug/use-identification";
import { useOtherLegalStore } from "src/hooks/snug/use-otherLegal";
import { useMedicalAllergyStore } from "src/hooks/snug/use-medicalAllergies";
import { useMedicalHistoryStore } from "src/hooks/snug/use-medicalHistory";
import { useMedicalMedicationStore } from "src/hooks/snug/use-medicalMedications";
import { useDebtStore } from "src/hooks/snug/use-debts";
import { useUtilitiesStore } from "src/hooks/snug/use-utilities";
import { useStorageStore } from "src/hooks/snug/use-storage";
import { useDigitalAssetStore } from "src/hooks/snug/use-digitalassets";
import { usePasscodeStore } from "src/hooks/snug/use-passcodes";
import { useLegacyStore } from "src/hooks/snug/use-legacy";

export interface ModuleCounts {
  personal: number;
  legal: number;
  medical: number;
  financial: number;
  property: number;
  digital: number;
  legacy: number;
}

export type ModuleKey = keyof ModuleCounts;

export interface MemberRef {
  udId: string;
  name: string;
}

const sameCounts = (a: ModuleCounts, b: ModuleCounts): boolean =>
  a.personal === b.personal &&
  a.legal === b.legal &&
  a.medical === b.medical &&
  a.financial === b.financial &&
  a.property === b.property &&
  a.digital === b.digital &&
  a.legacy === b.legacy;

/**
 * Invisible loader: fetches every Life Details store for a single member and
 * reports the per-module counts up. Rendered once per household member so the
 * (variable) set of members can each call the fixed set of hooks.
 */
const MemberCountsLoader: FC<{
  udId: string;
  onCounts: (udId: string, counts: ModuleCounts) => void;
}> = ({ udId, onCounts }) => {
  const { assets } = useAssetsStore(udId);
  const { peopleRoles } = usePeopleRolesStore(udId);
  const { history } = useHistoryStore(udId);
  const { identifications } = useIdentificationStore(udId);
  const { otherLegal } = useOtherLegalStore(udId);
  const { medicalAllergy } = useMedicalAllergyStore(udId);
  const { medicalHistory } = useMedicalHistoryStore(udId);
  const { medicalMedication } = useMedicalMedicationStore(udId);
  const { debt } = useDebtStore(udId);
  const { utilities } = useUtilitiesStore(udId);
  const { storage } = useStorageStore(udId);
  const { digitalAsset } = useDigitalAssetStore(udId);
  const { passcodes } = usePasscodeStore(udId);
  const { legacy } = useLegacyStore(udId);

  const counts = useMemo<ModuleCounts>(() => {
    const byType = (type: string) =>
      (assets ?? []).filter((a) => a.asset_type === type).length;
    return {
      personal:
        (peopleRoles?.length ?? 0) +
        (history?.length ?? 0) +
        (identifications?.length ?? 0),
      legal: (otherLegal?.length ?? 0) + byType("business_interest"),
      medical:
        (medicalAllergy?.length ?? 0) +
        (medicalHistory?.length ?? 0) +
        (medicalMedication?.length ?? 0),
      financial: byType("financial_account") + (debt?.length ?? 0),
      property:
        byType("realestate") +
        byType("personal_property") +
        (utilities?.length ?? 0) +
        (storage?.length ?? 0),
      digital: (digitalAsset?.length ?? 0) + (passcodes?.length ?? 0),
      legacy: legacy ? 1 : 0,
    };
  }, [
    assets,
    peopleRoles,
    history,
    identifications,
    otherLegal,
    medicalAllergy,
    medicalHistory,
    medicalMedication,
    debt,
    utilities,
    storage,
    digitalAsset,
    passcodes,
    legacy,
  ]);

  useEffect(() => {
    onCounts(udId, counts);
  }, [udId, counts, onCounts]);

  return null;
};

/**
 * Returns per-member module counts plus the loader nodes to render (they render
 * nothing visible). Memoize the `members` argument so the loaders aren't
 * remounted — and refetched — on every render.
 */
export const useHouseholdModuleCounts = (
  members: MemberRef[]
): { countsByUd: Record<string, ModuleCounts>; loaders: ReactNode } => {
  const [countsByUd, setCountsByUd] = useState<Record<string, ModuleCounts>>(
    {}
  );

  const onCounts = useCallback((udId: string, counts: ModuleCounts) => {
    setCountsByUd((prev) => {
      const existing = prev[udId];
      if (existing && sameCounts(existing, counts)) return prev;
      return { ...prev, [udId]: counts };
    });
  }, []);

  const loaders = useMemo(
    () =>
      members.map((m) => (
        <MemberCountsLoader key={m.udId} udId={m.udId} onCounts={onCounts} />
      )),
    [members, onCounts]
  );

  return { countsByUd, loaders };
};
